/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-file-selector-component.ts
 *
 * Created by kyang on 10/15/20.
 */

function BrandingFileSelectorController(
    this: JS5Object,
    _: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingFileSelectionService: JS5Object,
    BrandingViewService: JS5Object,
    Rx: JS5Object,
    VALID_FILE_EXTENSIONS: JS5Object,
) {
    const ASSET_PROPERTIES = ['containerType', 'assetType', 'rootAsset', 'asset'];
    const TYPES_WITH_PROFILE_FILTERS = ['video', 'audio', 'artwork'];
    const TYPES_WITH_PROXIES = ['video', 'watermark', 'artwork', 'ratingOverlay'];

    const FILTER_TYPE_KEYS: JS5Object = {
        'video': 'VIDEO',
        'cc': 'CC',
        'audio': 'AUDIO',
        'watermark': 'IMAGE',
        'artwork': 'OTHER',
        'ratingOverlay': 'RATING_OVERLAY',
    };

    const getCorrespondingAcceptProfile = () => {
        if (!this.asset) {
            return undefined;
        }
        return BrandingFileSelectionService.getAssetAcceptProfile(this.asset, this.assetType);
    };


    //============//
    // Validation //
    //============//

    this.getFileExtensions = () => {
        let fileExtensions: string[];
        if (_.includes(TYPES_WITH_PROFILE_FILTERS, this.assetType)
            && _.size(getCorrespondingAcceptProfile().fileExtensions) > 0) {
            fileExtensions = getCorrespondingAcceptProfile().fileExtensions;
        } else {
            let filterTypeKey = FILTER_TYPE_KEYS[this.assetType];
            fileExtensions = VALID_FILE_EXTENSIONS[filterTypeKey];
        }
        return fileExtensions;
    };

    this.validateRemoteFile = (fileSystemItem: JS5Object) => {
        let validation = BrandingFileSelectionService.validateRemoteFile(
            this.containerType,
            this.assetType,
            this.rootAsset,
            this.availableAcceptProfiles,
            this.asset,
            fileSystemItem
        );
        return validation.promise;
    };

    const onValidRemoteFileSelected = (virtualPath: string) => {
        BrandingFileSelectionService.onFileSelected(
            this.assetType, this.rootAsset, this.asset, virtualPath
        );
        if (this.onFileSelected) {
            this.onFileSelected({virtualPath: virtualPath});
        }
    };

    //===================//
    // Local File Upload //
    //===================//

    this.getLocalFilePreValidationErrors = (file: File, filter: JS5Object) => {
        // Doesn't add any local file filters
    };

    const updateIngestProgress = (progress: number) => {
        if (this.onIngestProgress) {
            this.onIngestProgress({progress: progress});
        }
    };

    this.validateLocalFile = (fileItem: JS5Object) => {
        let validation = BrandingFileSelectionService.validateLocalFile(
            this.containerType,
            this.assetType,
            this.rootAsset,
            this.availableAcceptProfiles,
            this.asset,
            fileItem,
            this.localFileUploaderConfig
        );
        let progressObservable = validation.progressObservable.takeUntil(this.assetReplacedObservable);
        progressObservable.subscribeOnNext(updateIngestProgress);
        progressObservable.subscribeOnCompleted(() => {
            updateIngestProgress(null);
        });
        return validation.promise;
    }

    const onLocalFileChanged = (fileItem: JS5Object, uploadedData: JS5Object) => {
        let filePath = _.get(uploadedData, 'remotePath', fileItem.file.name);
        BrandingFileSelectionService.onFileSelected(
            this.assetType, this.rootAsset, this.asset, filePath
        );
        if (this.file.status === 'PASSED' && this.onFileSelected) {
            this.onFileSelected({virtualPath: uploadedData.remotePath});
        }
    };


    //===================//
    // Template Response //
    //===================//

    this.getCorrespondingAcceptProfileName = () => {
        return _.get(getCorrespondingAcceptProfile(), 'name');
    };

    this.canSelectFile = () => {
        return !this.promptAcceptProfile || !!getCorrespondingAcceptProfile();
    };

    this.assetFileIsValid = () => {
        return _.get(this, 'file.status') === 'PASSED';
    };

    this.assetFileIsInvalid = () => {
        return _.get(this, 'file.status') === 'FAILED';
    };

    this.getAssetError = () => {
        if (this.assetFileIsInvalid()) {
            return 'File did not pass validation';
        } else if (this.assetFileIsValid()) {
            return this.extraPropertiesError;
        }
        return null;
    };

    this.assetHasInfo = () => {
        return BrandingAssetService.assetWasIngested(this.asset);
    };

    this.assetHasPreview = () => {
        return _.includes(TYPES_WITH_PROXIES, this.assetType) && BrandingAssetService.assetHasProxy(this.asset);
    };

    this.removeAsset = () => {
        if (this.onRemoveClicked) {
            this.onRemoveClicked();
        }
    };

    this.openAssetPreviewModal = () => {
        BrandingViewService.openAssetPreviewModal(this.asset, this.assetType);
    };

    this.openAssetInfoModal = () => {
        BrandingViewService.openAssetInfoModal(this.asset, this.assetType);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    const updateValidity = () => {
        if (this.editorForm) {
            this.editorForm.$setValidity('extraPropertiesErrored', !this.extraPropertiesError);
        }
    };

    const resetFileStatus = () => {
        if (this.assetReplacedObservable) {
            this.assetReplacedObservable.onNext();
        }
        if (_.get(this, 'asset.id')) {
            this.file = {
                type: 'REMOTE',
                files: {virtualPath: this.asset.virtualPath},
                results: {
                    source: this.asset.virtualPath,
                    errors: [],
                    warnings: [],
                },
                status: 'PASSED'
            };
        } else {
            this.file = null;
        }
    };

    this.$onChanges = (changes: JS5Object) => {
        if (_.some(ASSET_PROPERTIES, (property: string) => changes[property])) {
            if (this.localFileUploaderConfig) {
                BrandingFileSelectionService.reinitializeLocalUploaderConfig(
                    this.assetType, this.localFileUploaderConfig
                );
            }
            resetFileStatus();
        }
        if (changes.extraPropertiesError) {
            updateValidity();
        }
    };

    this.onFileChanged = () => {
        switch (_.get(this, 'file.type')) {
            case 'LOCAL':
                onLocalFileChanged(this.file.files[0], _.get(this.file, 'results.0.uploadedData'));
                break;
            case 'REMOTE':
                onValidRemoteFileSelected(this.file.files[0].virtualPath);
                break;
        }
    };

    this.initializeLocalUploaderConfig = (fileUploaderConfig: JS5Object) => {
        this.localFileUploaderConfig = fileUploaderConfig;
        BrandingFileSelectionService.reinitializeLocalUploaderConfig(
            this.assetType, this.localFileUploaderConfig
        );
    };

    this.$onInit = () => {
        this.assetReplacedObservable = new Rx.Subject();
        resetFileStatus();
        updateValidity();
    };

    this.$onDestroy = () => {
        this.assetReplacedObservable.onNext();
        this.assetReplacedObservable.onCompleted();
        if (_.get(this, 'editorForm.$$parentForm')) {
            this.editorForm.$$parentForm.$removeControl(this.editorForm);
        }
    };
}

angular
    .module('prism-ui.components.branding')
    .component('brandingFileSelector', {
        templateUrl: 'components/branding/editView/components/media/editView/components/brandingFileSelector/branding-file-selector-template.html',
        transclude: {
            // TODO: After Aspera integrated, add optional info buttons? (for package media QC button)
            // properties: This HTML is added to right-side of file info buttons
            'properties': '?brandingFileSelectorProperties'
        },
        controller: BrandingFileSelectorController,
        bindings: {
            /*
            Asset Config
            containerType: (string; required) Branding asset container's type
            assetType: (string; required) This specific asset's type
            rootAsset: (JS5Object; required) The asset that holds all the info
                       needed for the branding edit view
            asset:     (JS5Object; required) The asset to modify
            includeAcceptProfile: (boolean; required) If true, asset's
                                  corresponding acceptProfile will be displayed.
            promptAcceptProfile:  (boolean; optional) If true, asset.acceptProfile
                                  can be set to any of the availableAcceptProfiles.
                                  Note: If includeAcceptProfile == false,
                                  this variable won't do anything.
            availableAcceptProfiles: (JS5Object[]; optional) Array of values
                                     to use for asset's acceptProfile prompt.
                                     Array is also used to validate watermarks.
            onIngestProgress:        (function; optional) Is invoked periodically
                                     when a file is being uploaded.
                                     Reports progress as percent between 0 & 1.
                                     When ingest is stopped, function is called with null.
            onFileSelected:          (function; optional) Is invoked when
                                     a file is selected.
            extraPropertiesError:    (string; optional) String to display when hovering
                                     over the error triangle. Is only displayed after
                                     file passes validation.
                                     If specified, forces asset to an invalid state.
            */
            containerType: '@',
            assetType: '@',
            rootAsset: '<',
            asset: '<',
            includeAcceptProfile: '<',
            promptAcceptProfile: '<?',
            availableAcceptProfiles: '<?',
            onIngestProgress: '&?',
            onFileSelected: '&?',
            extraPropertiesError: '<?',

            /*
            File Removal
            canRemove: (boolean; optional) If true, displays an X button at the
                       top-right of the file-selector.
            onRemoveClicked: (function; optional) Is invoked when X button
                             is clicked. If used, canRemove should == true.
             */
            canRemove: '<',
            onRemoveClicked: '&?',
        }
    });
